import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  OUTREACH_OUTLIER_PAGE_MAX,
  OUTREACH_SELECTION_MAX_IDS,
  type OutreachOutlierGroupCode,
  type OutreachSelectionResponse,
  type ReachChannel,
} from '@aegislab/shared';
import {
  fetchOutlierRows,
  useCandidatePage,
  useCandidateQueryWatchStore,
  useCandidateQuery,
  useCandidateStatuses,
  useCreateCandidateQuery,
  useLatestCandidateQuery,
  useOutlierSummary,
  useOutreachProgress,
  useOutreachSelection,
  useRetryCandidateQuery,
  useSaveOutreachSelection,
  type CandidateStatus,
} from '@/entities/outreach-candidate';
import {
  outreachDraftKeys,
  useOutreachDraft,
  useOutreachPresenceLease,
} from '@/entities/outreach-draft';
import { Button } from '@/shared/ui/atoms';
import { CandidateCriteriaBar } from './CandidateCriteriaBar';
import { CandidateSelectionBar } from './CandidateSelectionBar';
import { CandidateTable } from './CandidateTable';
import { CountryProgressBar } from './CountryProgressBar';
import { ManualCandidateAdd } from './ManualCandidateAdd';
import { OtherChannelSection } from './OtherChannelSection';
import { OutlierGroupList } from './OutlierGroupList';
import {
  isPicked,
  selectedCountOf,
  useCandidateSelection,
  type CandidateSelection,
} from '../model/useCandidateSelection';
import { baselineOf, outlierTotals } from '../model/outlierGroups';
import { useManualCandidate } from '../model/useManualCandidate';
import * as s from './OutreachCandidatesPanel.css';
export interface OutreachCandidatesPanelProps {
  scenarioId: string;
  /** 캠페인 `targetCountries`. 비면 이 면이 아예 안 선다. */
  countries: string[];
  /** 로스터 행으로 가는 링크. 폼 제출 행만 쓴다. */
  rosterHref?: (rosterEntryId: string) => string;
  /** 「이 대상으로 메일 작성」. 없으면 버튼이 안 선다. */
  onCompose?: (country: string) => void;
}
const n = new Intl.NumberFormat('ko-KR');
/**
 * 후보 선택 — IA `docs/design/ia/creator-recruitment-ia.md` §3-3, 승인 시안 CandidatesAfter.
 *
 * 나라 줄 아래 몸통은 **나라마다 새로 선다**(`key={country}`). 예전에는 나라를 바꿔도 앞 나라에서
 * 누른 선택이 훅에 남아 새 나라 초안으로 저장됐다(보고서 `OutreachCandidatesPanel.tsx:93-113`).
 */
export function OutreachCandidatesPanel({
  scenarioId,
  countries,
  rosterHref,
  onCompose,
}: OutreachCandidatesPanelProps) {
  const [country, setCountry] = useState(countries[0] ?? '');
  const progress = useOutreachProgress(scenarioId);
  const saved = useOutreachSelection(scenarioId, country || null);
  const latest = useLatestCandidateQuery(scenarioId, country || null);
  // 마지막 조회 번호를 못 받으면 저장된 스냅샷으로 연다 — 이어 보기를 잃을 뿐 면은 선다.
  const latestSettled = latest.data !== undefined || latest.isError;
  // 칩은 캠페인 나라로 세운다 — 나라를 고르는 자리가 이 칩 하나뿐이라(퀘스트 2f546c56 B안)
  // 진행 수를 못 받았다고 칩이 사라지면 나라를 바꿀 길도 같이 사라진다.
  const countryChips = (
    <CountryProgressBar
      countries={countries}
      progress={progress.data?.countries}
      current={country}
      onSelect={setCountry}
    />
  );
  return (
    /*
      메일 면의 형제 셋(후보 선택 · 회신 큐 · 발송 이력)은 전부 `section` 이다(2026-09-28).
      div 와 section 이 섞여 있어 읽는 사람도 코드를 보는 사람도 층위를 못 가렸다.
      화면에 제목을 둘 자리가 아니라 읽어 주는 화면에만 이름을 준다.
    */
    <section className={s.panel} aria-labelledby="outreach-candidates-heading">
      {/*
        화면 제목은 바로 위 단계 줄의 「후보 선택」 이 든다(퀘스트 2f546c56 B안) — 같은 말을 두 번
        세우지 않는다. section 에는 제목이 있어야 하므로 읽어 주는 화면에만 남긴다.
      */}
      <h3 id="outreach-candidates-heading" className={s.srOnly}>
        후보 선택
      </h3>
      {saved.data === undefined || !latestSettled ? (
        <>
          {/* 나라를 바꾸는 사이에도 칩이 자리를 지킨다 — 사라졌다 서면 줄이 흔들린다. */}
          {countryChips}
          <p className={s.note}>
            {saved.isError
              ? '저장된 선택을 불러오지 못했습니다.'
              : '저장된 선택을 불러오는 중입니다.'}
          </p>
        </>
      ) : (
        <CountryCandidates
          key={country}
          scenarioId={scenarioId}
          country={country}
          countryChips={countryChips}
          saved={saved.data}
          initialQueryId={latest.data?.queryId ?? saved.data.snapshotId}
          rosterHref={rosterHref}
          onCompose={onCompose}
        />
      )}
    </section>
  );
}

interface CountryCandidatesProps {
  scenarioId: string;
  country: string;
  /** 조건 줄 맨 앞에 서는 나라 칩. 나라를 바꾸는 자리는 이것 하나다. */
  countryChips: ReactNode;
  saved: OutreachSelectionResponse;
  /**
   * 처음 열 조회. 내가 마지막으로 건 조회가 저장된 스냅샷보다 앞선다 — 고른 뒤 「다시 조회」 를
   * 걸고 나갔다 와도 새 조회를 이어 본다. 화면 안에서 새로 건 직후와 같은 판정이다.
   */
  initialQueryId: string | null;
  rosterHref?: (rosterEntryId: string) => string;
  onCompose?: (country: string) => void;
}

function CountryCandidates({
  scenarioId,
  country,
  countryChips,
  saved,
  initialQueryId,
  rosterHref,
  onCompose,
}: CountryCandidatesProps) {
  const queryClient = useQueryClient();
  // 새로고침 뒤 조회가 비면 저장이 조용히 멈췄다. 준비 전에 나갔다 오면 「조회 중」 이 사라졌다.
  const [queryId, setQueryId] = useState<string | null>(initialQueryId);
  const [cursors, setCursors] = useState<(string | null)[]>([null]);
  const [listOpen, setListOpen] = useState(false);
  const [excludedByGroup, setExcludedByGroup] = useState(
    () => new Map<OutreachOutlierGroupCode, string[]>()
  );
  const [pendingCode, setPendingCode] = useState<OutreachOutlierGroupCode | null>(null);
  const [groupError, setGroupError] = useState<string | null>(null);

  const createQuery = useCreateCandidateQuery(scenarioId);
  const retryQuery = useRetryCandidateQuery(scenarioId, country);
  const detail = useCandidateQuery(scenarioId, queryId);
  // 끝나기를 기다리는 조회는 앱 뿌리의 감시자에 맡긴다 — 이 면이나 상세 덮개가 닫혀도 알림이 온다.
  const watchQuery = useCandidateQueryWatchStore((st) => st.watch);
  const detailState = detail.data?.state;
  useEffect(() => {
    if (queryId !== null && (detailState === 'queued' || detailState === 'running')) {
      watchQuery({ scenarioId, country, queryId });
    }
  }, [queryId, detailState, scenarioId, country, watchQuery]);
  const ready = detail.data?.state === 'ready';
  const totalCount = detail.data?.totalCount ?? null;

  const cursor = cursors[cursors.length - 1] ?? null;
  const page = useCandidatePage(scenarioId, queryId, cursor, { enabled: ready });
  const rows = useMemo(() => page.data?.items ?? [], [page.data]);

  const draft = useOutreachDraft(scenarioId, country);
  const draftId = draft.data?.id ?? null;
  const save = useSaveOutreachSelection(scenarioId, country);
  const manual = useManualCandidate(scenarioId);
  /*
    작업 중 임대는 사람이 선택을 바꿔 저장한 뒤부터 건다(IA §3-7, 조사 §3-2). 메일 면에는 회신
    큐도 있어서, 면을 열기만 해도 걸면 회신만 보러 온 사람까지 「작업 중」 으로 뜬다. 옛 전체 선택을 새 조회로 옮기는 자동 저장은 사람의 손이 아니라 세지 않는다.
  */
  const [touched, setTouched] = useState(false);
  const autoSaving = useRef(false);
  useOutreachPresenceLease(scenarioId, country, touched);

  // 첫 저장이 초안 행을 만든다 — 묶음을 부를 초안 번호가 그때 생긴다.
  const refreshDraftRow = () => {
    if (draftId !== null) return;
    void queryClient.invalidateQueries({ queryKey: outreachDraftKeys.one(scenarioId, country) });
  };

  const persist = (next: CandidateSelection) => {
    if (queryId === null) return;
    const byHand = !autoSaving.current;
    save.mutate(
      {
        country,
        snapshotId: queryId,
        mode: next.mode,
        includedIds: [...next.includedIds],
        excludedIds: [...next.excludedIds],
      },
      {
        onSuccess: () => {
          if (byHand) setTouched(true);
          refreshDraftRow();
        },
      }
    );
  };

  const {
    selection,
    current,
    adopt,
    toggle,
    setPicked,
    pickPage,
    selectAllMatching,
    exclude,
    include,
  } = useCandidateSelection(saved, persist);

  // 기본은 아무도 안 고른 상태다 — 담당자가 이번 인원을 정한다. 이미 전체 선택으로 저장된 초안만,
  // 새 조회를 걸었을 때 같은 선택(조건 전체)을 새 스냅샷으로 옮긴다. 초안을 바꾸지 않는 이어짐이다.
  const autoFor = useRef<string | null>(null);
  const needsMove =
    ready && queryId !== null && saved.mode === 'ALL_MATCHING' && saved.snapshotId !== queryId;
  useEffect(() => {
    if (!needsMove || autoFor.current === queryId) return;
    autoFor.current = queryId;
    autoSaving.current = true;
    selectAllMatching();
    autoSaving.current = false;
  });

  const [reachChannel, setReachChannel] = useState<ReachChannel>('EMAIL');
  const [pickError, setPickError] = useState<string | null>(null);
  const pickCount = (count: number) => {
    if (queryId === null) return;
    setPickError(null);
    save.mutate(
      {
        country,
        snapshotId: queryId,
        mode: 'PICKED',
        includedIds: [],
        excludedIds: [...current().excludedIds],
        pickCount: count,
      },
      {
        onSuccess: (row) => {
          adopt(row);
          setTouched(true);
          refreshDraftRow();
        },
        onError: () => setPickError('인원을 고르지 못했습니다. 잠시 뒤 다시 눌러 주세요.'),
      }
    );
  };

  const statuses = useCandidateStatuses(
    scenarioId,
    useMemo(() => rows.map((row) => row.externalCreatorId), [rows])
  );
  const statusMap = useMemo<ReadonlyMap<string, CandidateStatus> | null>(() => {
    if (statuses.data === undefined) return null;
    return new Map(statuses.data.statuses.map((status) => [status.externalCreatorId, status]));
  }, [statuses.data]);

  // 기본 제외는 표에서 빠진다 — 「먼저 볼 것」 의 「이 캠페인에서 이미 답함」 이 같은 사람을 보인다.
  const visibleRows = useMemo(() => {
    if (statusMap === null) return rows;
    return rows.filter((row) => statusMap.get(row.externalCreatorId)?.defaultExcluded !== true);
  }, [rows, statusMap]);

  // 주소가 없다고 확인된 행은 페이지 단위 선택에서 뺀다(모르면 `null` 이라 남긴다).
  const pickableIds = visibleRows
    .filter((row) => row.hasAddress !== false)
    .map((row) => row.externalCreatorId);

  const selectedSet = useMemo(
    () => new Set(rows.map((row) => row.externalCreatorId).filter((id) => isPicked(selection, id))),
    [rows, selection]
  );

  // 묶음은 명단 모양이 바뀔 때만 다시 받는다. 고른 사람 모드는 명단이 작아 저장마다 받아도 싸다.
  const membership = `${saved.snapshotId ?? ''}:${saved.mode}:${
    saved.mode === 'PICKED' ? (saved.updatedAt ?? '') : 'all'
  }`;
  const summary = useOutlierSummary(
    scenarioId,
    saved.updatedAt === null ? null : draftId,
    membership
  );

  const totals = summary.data
    ? outlierTotals(
        summary.data,
        baselineOf(summary.data, selection.excludedIds),
        selection.excludedIds
      )
    : null;

  const excludeGroup = async (code: OutreachOutlierGroupCode) => {
    if (draftId === null) return;
    setPendingCode(code);
    setGroupError(null);
    try {
      // ponytail: 200행 페이지를 끝까지 넘긴다. 페이지마다 서버가 명단 전체를 다시 가르므로
      // 수천 명 묶음이면 느리다 — 번호만 주는 가벼운 응답이 생기면 그걸로 바꾼다.
      const ids: string[] = [];
      let offset: number | null = 0;
      while (offset !== null) {
        const res = await fetchOutlierRows(scenarioId, {
          draftId,
          code,
          offset,
          limit: OUTREACH_OUTLIER_PAGE_MAX,
        });
        ids.push(...res.rows.map((row) => row.externalCreatorId));
        offset = res.nextOffset;
      }
      // 응답을 기다린 사이 바뀐 선택 위에서 센다 — 누른 시점 렌더의 선택이 아니다.
      if (new Set([...current().excludedIds, ...ids]).size > OUTREACH_SELECTION_MAX_IDS) {
        setGroupError(
          `한 번에 뺄 수 있는 사람은 ${n.format(OUTREACH_SELECTION_MAX_IDS)}명까지라 이 묶음을 뺄 수 없습니다.`
        );
        return;
      }
      exclude(ids);
      setExcludedByGroup((map) => new Map(map).set(code, ids));
    } catch {
      setGroupError('묶음 명단을 받지 못했습니다. 잠시 뒤 다시 눌러 주세요.');
    } finally {
      setPendingCode(null);
    }
  };

  return (
    <>
      <CandidateCriteriaBar
        countrySlot={countryChips}
        state={detail.data?.state ?? null}
        queryId={queryId}
        requestedAt={detail.data?.requestedAt}
        progress={detail.data?.progress}
        progressUpdatedAt={detail.dataUpdatedAt}
        loadFailed={detail.isError}
        onReload={() => void detail.refetch()}
        disabled={createQuery.isPending}
        onSubmit={(input) =>
          createQuery.mutate(
            { region: country, ...input },
            {
              onSuccess: (accepted) => {
                setQueryId(accepted.queryId);
                setCursors([null]);
              },
            }
          )
        }
        onRetry={() => {
          if (queryId !== null) retryQuery.mutate(queryId);
        }}
      />

      {ready ? (
        <CandidateSelectionBar
          reachChannel={reachChannel}
          onReachChannelChange={setReachChannel}
          mode={selection.mode}
          totalCount={totalCount}
          selectedCount={selectedCountOf(selection) ?? 0}
          pageCount={pickableIds.length}
          sendableCount={totals?.send ?? null}
          dropCount={totals?.drop ?? null}
          onPickPage={() => {
            pickPage(pickableIds);
            // 표가 접혀 있으면 누구를 골랐는지 보이도록 함께 연다.
            setListOpen(true);
          }}
          onPickCount={pickCount}
          pickPending={save.isPending}
          pickError={pickError}
          nullRegionCount={detail.data?.nullRegionCount ?? null}
          snapshotAt={detail.data?.snapshotAt ?? null}
          expiresAt={detail.data?.expiresAt ?? null}
        />
      ) : null}

      {ready && reachChannel === 'OTHER_MEDIA' ? (
        <OtherChannelSection scenarioId={scenarioId} queryId={queryId} />
      ) : null}

      {reachChannel === 'EMAIL' && summary.data && draftId !== null && totals ? (
        <section className={s.section} aria-label="먼저 볼 것">
          <p className={s.heading}>
            <span className={s.headingTitle}>먼저 볼 것</span>
            <span className={s.headingCount}>{n.format(totals.review)}명</span>
          </p>
          <OutlierGroupList
            scenarioId={scenarioId}
            draftId={draftId}
            summary={summary.data}
            excludedIds={selection.excludedIds}
            onExclude={exclude}
            onInclude={include}
            onExcludeGroup={(code) => void excludeGroup(code)}
            excludedByGroup={excludedByGroup}
            pendingCode={pendingCode}
            listOpen={listOpen}
            onToggleList={() => setListOpen((open) => !open)}
          />
          {groupError ? (
            <p className={s.error} role="alert">
              {groupError}
            </p>
          ) : null}
        </section>
      ) : reachChannel === 'EMAIL' && summary.isPending && summary.fetchStatus === 'fetching' ? (
        <p className={s.note}>먼저 볼 사람을 가리는 중입니다.</p>
      ) : reachChannel === 'EMAIL' && summary.isError ? (
        <p className={s.error} role="alert">
          먼저 볼 사람을 가리지 못했습니다.{' '}
          <Button variant="ghost" size="xs" onClick={() => void summary.refetch()}>
            다시 가르기
          </Button>
        </p>
      ) : null}

      {ready && reachChannel === 'EMAIL' && (listOpen || summary.data === undefined) ? (
        <>
          <CandidateTable
            rows={visibleRows}
            selectedIds={selectedSet}
            statuses={statusMap}
            rosterHref={rosterHref}
            onToggle={toggle}
            onToggleAll={(picked) => setPicked(pickableIds, picked)}
          />
          <div className={s.pager}>
            <Button
              variant="ghost"
              size="xs"
              disabled={cursors.length === 1}
              onClick={() => setCursors((list) => list.slice(0, -1))}
            >
              이전 페이지
            </Button>
            <Button
              variant="ghost"
              size="xs"
              disabled={!page.data?.nextCursor}
              onClick={() => {
                const next = page.data?.nextCursor;
                if (next) setCursors((list) => [...list, next]);
              }}
            >
              다음 페이지
            </Button>
          </div>
        </>
      ) : null}

      {/* 바닥 줄 — 왼쪽은 보조 경로(직접 추가), 오른쪽은 이 화면의 끝(보낼 수 · 메일 작성). */}
      <div className={s.footer}>
        <ManualCandidateAdd
          onAdd={(input) => manual.add(input)}
          pending={manual.pending}
          result={manual.result}
          error={manual.error}
        />
        {totals ? (
          <div className={s.footerEnd}>
            <p className={s.note}>
              보낼 수 있는 계정 <strong className={s.strong}>{n.format(totals.send)}</strong> · 뺄
              계정 <strong className={s.strong}>{n.format(totals.drop)}</strong>
            </p>
            {onCompose ? (
              <Button
                variant="primary"
                size="sm"
                disabled={save.isPending || totals.send === 0}
                onClick={() => onCompose(country)}
              >
                {`보낼 수 있는 ${n.format(totals.send)}명으로 메일 작성`}
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
    </>
  );
}
